/**
 * Cliente del API del núcleo para el launcher (docs/FASE-7.md §7).
 *
 * Todo lo que pinta el launcher sale de endpoints que ya existían o que añadió esta fase; el
 * launcher **no tiene backend propio** ni endpoints hechos a su medida. Cada respuesta se valida
 * con un esquema de `@continnua/contracts` antes de usarse, como hace la consola: una respuesta sin
 * validar es una forma cómoda de que un cambio del núcleo se manifieste como una pantalla en blanco.
 */

import {
  AssignableRolesResponseSchema,
  MembershipSchema,
  MeOrgsResponseSchema,
  MeResponseSchema,
  MySessionsResponseSchema,
  MyTotpSetupResponseSchema,
  MyTotpStatusSchema,
  type AssignableRolesResponse,
  type InviteMemberInput,
  type MeOrgsResponse,
  type MeResponse,
  type Membership,
  type MySessionsResponse,
  type MyTotpSetupResponse,
  type MyTotpStatus,
  type UpdateMemberInput,
  type UpdateMeInput,
} from "@continnua/contracts";
import { z } from "zod";

/** `fetch` con el Bearer ya puesto; lo entrega `useAuthorizedFetch` de `@continnua/auth-web/react`. */
export type AuthorizedFetch = (input: string, init?: RequestInit) => Promise<Response>;

export class LauncherApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "LauncherApiError";
  }
}

/** Base del API. En desarrollo `/api` va por el proxy de Vite; en producción, al IdP real. */
export function apiBaseUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL;
  return typeof base === "string" && base.length > 0 ? base.replace(/\/+$/, "") : "/api";
}

/**
 * Cuerpo JSON de una respuesta, o un error que diga qué pasó de verdad.
 *
 * El caso que motiva esto: si el proxy del dominio del portal no enruta `/api` al núcleo, la
 * petición la acaba atendiendo el `try_files ... /index.html` de su propio nginx, que responde
 * **200 con el HTML de la aplicación**. `res.json()` reventaba entonces con «Unexpected token '<',
 * "<!doctype "... is not valid JSON», que se pintaba tal cual y no señala a ninguna parte — hay que
 * conocer el despliegue entero para traducirlo.
 *
 * Se mira el `content-type` y no se intenta parsear a ciegas: un 200 que no es JSON, viniendo de
 * este API, solo puede ser eso.
 */
export async function leerJson(res: Response, urlPedida: string): Promise<unknown> {
  const tipo = res.headers.get("content-type") ?? "";
  if (!tipo.includes("json")) {
    throw new LauncherApiError(
      `La API respondió ${String(res.status)} con "${tipo || "tipo desconocido"}" en vez de JSON al pedir ` +
        `${urlPedida}. Suele significar que ese origen no enruta "/api" al núcleo y está devolviendo la propia ` +
        `aplicación; revisa el proxy del dominio o VITE_API_BASE_URL (docs/OPERACION.md).`,
      res.status,
    );
  }
  return res.json();
}

async function request<T>(
  authorizedFetch: AuthorizedFetch,
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  const url = `${apiBaseUrl()}/v1${path}`;
  const res = await authorizedFetch(url, {
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });

  if (!res.ok) {
    // El núcleo devuelve `{ error: { code, message } }` (docs/FASE-0.md §5). Si no se puede leer,
    // el estado HTTP es lo único que queda y basta para decidir qué pintar.
    const cuerpo: unknown = await res.json().catch(() => null);
    const mensaje =
      typeof cuerpo === "object" && cuerpo !== null && "error" in cuerpo
        ? String((cuerpo as { error: { message?: unknown } }).error.message ?? "")
        : "";
    throw new LauncherApiError(mensaje || `La petición falló (${String(res.status)}).`, res.status);
  }

  return schema.parse(await leerJson(res, url));
}

export const api = {
  me: (f: AuthorizedFetch): Promise<MeResponse> => request(f, "/me", MeResponseSchema),
  orgs: (f: AuthorizedFetch): Promise<MeOrgsResponse> => request(f, "/me/orgs", MeOrgsResponseSchema),

  updateMe: (f: AuthorizedFetch, input: UpdateMeInput): Promise<MeResponse> =>
    request(f, "/me", MeResponseSchema, { method: "PATCH", body: JSON.stringify(input) }),

  changePassword: async (f: AuthorizedFetch, currentPassword: string, newPassword: string): Promise<void> => {
    const res = await f(`${apiBaseUrl()}/v1/me/password`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (!res.ok) {
      const cuerpo = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new LauncherApiError(cuerpo?.error?.message ?? "No se pudo cambiar la contraseña.", res.status);
    }
  },

  totpStatus: (f: AuthorizedFetch): Promise<MyTotpStatus> => request(f, "/me/totp", MyTotpStatusSchema),
  totpSetup: (f: AuthorizedFetch): Promise<MyTotpSetupResponse> =>
    request(f, "/me/totp/setup", MyTotpSetupResponseSchema, { method: "POST" }),
  totpVerify: (f: AuthorizedFetch, code: string): Promise<MyTotpStatus> =>
    request(f, "/me/totp/verify", MyTotpStatusSchema, { method: "POST", body: JSON.stringify({ code }) }),
  totpDisable: (f: AuthorizedFetch, password: string): Promise<MyTotpStatus> =>
    request(f, "/me/totp", MyTotpStatusSchema, { method: "DELETE", body: JSON.stringify({ password }) }),

  sessions: (f: AuthorizedFetch): Promise<MySessionsResponse> => request(f, "/me/sessions", MySessionsResponseSchema),
  revokeSession: async (f: AuthorizedFetch, sessionId: string): Promise<void> => {
    const res = await f(`${apiBaseUrl()}/v1/me/sessions/${sessionId}`, { method: "DELETE" });
    if (!res.ok) throw new LauncherApiError("No se pudo cerrar la sesión.", res.status);
  },

  // --- Miembros de la organización (ADR-007) ---------------------------------------------------
  //
  // Las mismas rutas que usa la consola de operador: el núcleo distingue por quién llama, no por
  // desde dónde. Un token de usuario pasa si administra la organización Y el cliente OAuth tiene
  // `platform:members:read` / `:write`, que solo `launcher-web` tiene concedidos.

  members: (f: AuthorizedFetch, orgId: string): Promise<Membership[]> =>
    request(f, `/orgs/${orgId}/members`, z.object({ items: z.array(MembershipSchema) })).then((r) => r.items),

  assignableRoles: (f: AuthorizedFetch, orgId: string): Promise<AssignableRolesResponse> =>
    request(f, `/orgs/${orgId}/assignable-roles`, AssignableRolesResponseSchema),

  inviteMember: (f: AuthorizedFetch, orgId: string, input: InviteMemberInput): Promise<Membership> =>
    request(f, `/orgs/${orgId}/members`, MembershipSchema, { method: "POST", body: JSON.stringify(input) }),

  updateMember: (f: AuthorizedFetch, orgId: string, userId: string, input: UpdateMemberInput): Promise<Membership> =>
    request(f, `/orgs/${orgId}/members/${userId}`, MembershipSchema, { method: "PATCH", body: JSON.stringify(input) }),

  /** `DELETE` responde 204 sin cuerpo, así que no pasa por `request`, que siempre espera JSON. */
  removeMember: async (f: AuthorizedFetch, orgId: string, userId: string): Promise<void> => {
    const res = await f(`${apiBaseUrl()}/v1/orgs/${orgId}/members/${userId}`, { method: "DELETE" });
    if (!res.ok) {
      const cuerpo = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new LauncherApiError(cuerpo?.error?.message ?? "No se pudo quitar a la persona.", res.status);
    }
  },

  /** `204` sin cuerpo, igual que `removeMember`. */
  resendInvitation: async (f: AuthorizedFetch, orgId: string, userId: string): Promise<void> => {
    const res = await f(`${apiBaseUrl()}/v1/orgs/${orgId}/members/${userId}/resend-invitation`, { method: "POST" });
    if (!res.ok) {
      const cuerpo = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
      throw new LauncherApiError(cuerpo?.error?.message ?? "No se pudo reenviar la invitación.", res.status);
    }
  },
};
